import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Divider,
  IconButton,
  Grid,
  Autocomplete,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { toast } from "react-toastify";

import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { getBedOccupancyApi, exportBedOccupancyApi } from "../services/reports.service";

import ReportsBedOccupancyTable from "./ReportsBedOccupancyTable";
import ReportsLiveChart from "./ReportsLiveChart";

/** X-axis labels for bed occupancy live area chart (Jan–Dec, UTC year of confirmed date). */
const LIVE_CHART_MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
/* Demo chart series (retired): was static 12 points for area chart before `monthlyOccupiedTrend` from API.
const chartValue = [20, 50, 40, 90, 80, 70, 60, 80, 60, 90, 10, 50];
*/
const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)", // Background color
  width: "44px", // Width
  height: "44px", // Height
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};
/* Retired: demo values were labeled "Search Template" but were fake UHID/name pairs — emptying broke the dropdown.
const uhidOptions = [
  { label: "John Doe", value: "001" },
  { label: "John Doe 2", value: "002" },
  { label: "John Doe 003", value: "003" },
  { label: "John Doe 004", value: "004" },
];
*/
/** Report modes for this screen. Backend `bed-occupancy` currently keys only on `confirmedDate`; values are for UX + future filters. */
const BED_OCC_SEARCH_TEMPLATES = [
  { label: "Occupancy by confirmed date", value: "by_confirmed_date" },
];

export default function ReportsBedOccupancy() {
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate(-1);
  };

  // State management
  const [formData, setFormData] = useState({
    searchTemplate: [BED_OCC_SEARCH_TEMPLATES[0]],
    confirmedDate: null,
  });
  
  const [reportData, setReportData] = useState({
    liveChartSeries: Array(12).fill(0),
    tableData: [],
    totalBeds: 569, // Dynamic bed count
    occupiedBeds: 432,
    availableBeds: 137,
  });
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors
  
  const [activeTab, setActiveTab] = useState(1);
  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch report data
  const fetchReportData = async () => {
    try {
      setLoading(true);
      const cd = formData.confirmedDate;
      const params = {
        searchTemplate: formData.searchTemplate?.map((item) => item.value),
        confirmedDate:
          cd instanceof Date && !Number.isNaN(cd.getTime()) ? cd.toISOString() : cd,
      };

      const response = await getBedOccupancyApi(params);
      
      if (response?.data) {
        const trend = response.data.monthlyOccupiedTrend;
        const liveChartSeries = Array.from({ length: 12 }, (_, i) =>
          Array.isArray(trend) && trend[i] !== undefined && trend[i] !== null
            ? Number(trend[i]) || 0
            : 0
        );
        setReportData({
          liveChartSeries,
          tableData: response.data.tableData || [],
          totalBeds: response.data.totalBeds || 569,
          occupiedBeds: response.data.occupiedBeds || 432,
          availableBeds: response.data.availableBeds || 137,
        });
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.searchTemplate || formData.searchTemplate.length === 0) {
      errorFields.push('searchTemplate');
    }
    if (!formData.confirmedDate) {
      errorFields.push('confirmedDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    fetchReportData();
  };

  // Handle export
  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      const cd = formData.confirmedDate;
      const exportParams = {
        searchTemplate: formData.searchTemplate?.map((item) => item.value),
        confirmedDate:
          cd instanceof Date && !Number.isNaN(cd.getTime()) ? cd.toISOString() : cd,
      };

      const result = await exportBedOccupancyApi(exportParams);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchReportData();
  }, []);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          {/* Search and Buttons */}
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              Bed Occupancy - Date View
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              my: "20px",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={BED_OCC_SEARCH_TEMPLATES}
                  multiple
                  limitTags={2}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, searchTemplate: newValue }));
                    // Clear error when field is changed
                    if (errors.includes('searchTemplate')) {
                      setErrors(errors.filter(e => e !== 'searchTemplate'));
                    }
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('searchTemplate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  name="confirmedDate"
                  label="Confirmed Date"
                  value={formData.confirmedDate}
                  onChange={(newValue) => {
                    handleDateChange("confirmedDate", newValue);
                    // Clear error when field is changed
                    if (errors.includes('confirmedDate')) {
                      setErrors(errors.filter(e => e !== 'confirmedDate'));
                    }
                  }}
                  slotProps={{
                    textField: { 
                      required: true, 
                      fullWidth: true,
                      InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                    },
                  }}
                  sx={{ 
                    bgcolor: "white",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: errors?.includes('confirmedDate') ? '1px solid red' : '1px solid inherit',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>

          <Box
            sx={{
              // border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              mb: "30px",
              p: "20px",
            }}
          >
            {/* dynamic button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  border: "1px solid grey",
                  borderRadius: "5px",
                  px: 2,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  width: "300px",
                  cursor: "pointer",
                  mb: "20px",
                }}
              >
                <Typography
                  onClick={() => handleTabClick(1)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    ...(activeTab === 1 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Live Chart
                </Typography>
                <Divider flexItem orientation="vertical" />
                <Typography
                  onClick={() => handleTabClick(2)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    ...(activeTab === 2 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Table
                </Typography>
                <Divider flexItem orientation="vertical" />
                <Typography
                  onClick={() => handleTabClick(3)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    ...(activeTab === 3 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Summary ({reportData.totalBeds})
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  mb: "20px",
                }}
              >
                {activeTab === 2 && (
                  <Typography sx={{ fontSize: "14px" }}>
                    {/* was: Rows: 3 | Column: 8 (static demo) */}
                    Rows: {reportData.tableData?.length ?? 0} | Columns: 7
                  </Typography>
                )}
                {activeTab === 2 && (
                  <Box sx={iconStyleProps}>
                    <EditIcon
                      sx={{
                        cursor: "pointer",
                        color: "transparent",
                        fontSize: "18px",
                      }}
                    />
                  </Box>
                )}
                <Box sx={iconStyleProps}>
                  <ShareIcon />
                </Box>
                <Box sx={iconStyleProps}>
                  <IconButton>
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {activeTab === 1 && (
              <ReportsLiveChart
                months={LIVE_CHART_MONTH_LABELS}
                chartValue={reportData.liveChartSeries}
              />
            )}
            {/* Retired: donut chart for two-point Occupied/Available only — replaced by monthly area series + API `monthlyOccupiedTrend`.
            <ReportsBedOccupancyChart chartData={reportData.chartData} />
            */}
            {activeTab === 2 && <ReportsBedOccupancyTable tableData={reportData.tableData} />}
            {activeTab === 3 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 3,
                alignItems: 'center',
                pt: 4 
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Bed Occupancy Summary
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 3,
                  width: '100%',
                  maxWidth: 800
                }}>
                  <Box sx={{
                    backgroundColor: '#e3f2fd',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '2px solid #2196f3'
                  }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {reportData.totalBeds}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#1976d2' }}>
                      Total Beds
                    </Typography>
                  </Box>
                  <Box sx={{
                    backgroundColor: '#e8f5e8',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '2px solid #4caf50'
                  }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#388e3c' }}>
                      {reportData.occupiedBeds}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#388e3c' }}>
                      Occupied Beds
                    </Typography>
                  </Box>
                  <Box sx={{
                    backgroundColor: '#fff3e0',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '2px solid #ff9800'
                  }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#f57c00' }}>
                      {reportData.availableBeds}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#f57c00' }}>
                      Available Beds
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  mt: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Occupancy Rate: {Math.round((reportData.occupiedBeds / reportData.totalBeds) * 100)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
